import React, { useCallback, useEffect, useState } from 'react';
import { Key, Plus, Trash2, Copy, Check, AlertTriangle } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { api, apiUrl } from '../api';

// The server only ever returns a key's plaintext at creation time, so this
// component holds it in state until the user dismisses it. Reloading the list
// loses it for good — which is the point.
export default function SettingsPage({ userId, userEmail }) {
  const [keys, setKeys] = useState([]);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [freshKey, setFreshKey] = useState(null); // { key, name } — shown once
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.listApiKeys(userId);
      setKeys(res.keys || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.createApiKey(name.trim() || 'API key', userId, userEmail);
      setFreshKey({ key: res.key, name: res.name });
      setCopied(false);
      setName('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.revokeApiKey(id, userId);
      setRevoking(null);
      load();
    } catch (err) {
      setError(err.message);
      setRevoking(null);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(freshKey.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

  return (
    <div>
      <div className="page-head">
        <div className="page-title">
          <h2>Settings</h2>
          <p>API access for your screens</p>
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}

      <div className="pair-card">
        <div className="pair-head"><Key size={16} /> API keys</div>
        <p>
          A key lets your own systems push content to your screens over the REST API.
          Keys are tied to your account — they only ever see the screens and templates you own.
          The base URL is <code className="mono">{apiUrl('') || window.location.origin}</code>;
          full reference at <a href={apiUrl('/api/docs')} target="_blank" rel="noreferrer">/api/docs</a>.
        </p>

        <form onSubmit={create} className="pair-form">
          <input className="input" value={name} maxLength={100}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name this key (e.g. Booking system)" />
          <button className="btn btn-primary" type="submit" disabled={busy}>
            <Plus size={16} /> {busy ? 'Creating…' : 'Create key'}
          </button>
        </form>

        {freshKey && (
          <div className="key-reveal">
            <div className="key-reveal-head">
              <AlertTriangle size={15} />
              Copy this now — it is shown once and cannot be retrieved again.
            </div>
            <div className="key-reveal-row">
              <code className="mono key-value">{freshKey.key}</code>
              <button className="btn btn-sm" onClick={copy} type="button">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
              <button className="btn btn-ghost btn-sm" type="button" onClick={() => setFreshKey(null)}>
                Done
              </button>
            </div>
          </div>
        )}

        {keys.length === 0 && !freshKey && (
          <p className="key-empty">No API keys yet. Create one to start using the API.</p>
        )}

        {keys.length > 0 && (
          <ul className="key-list">
            {keys.map((k) => (
              <li key={k.id} className="key-row">
                <div style={{ minWidth: 0 }}>
                  <div className="key-name">{k.name}</div>
                  <div className="mono key-meta">
                    {k.key_prefix}…
                    <span className="sep">·</span>
                    created {fmtDate(k.created_at)}
                    <span className="sep">·</span>
                    {k.last_used_at ? `last used ${fmtDate(k.last_used_at)}` : 'never used'}
                  </div>
                </div>
                <button className="btn btn-danger btn-icon" title="Revoke this key"
                  onClick={() => setRevoking(k)}>
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!revoking}
        danger
        title={`Revoke “${revoking?.name}”?`}
        body="Anything using this key stops working immediately. This cannot be undone — create a new key if you need to restore access."
        confirmLabel="Revoke"
        onConfirm={() => revoke(revoking.id)}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
}
